/**
 * RK Shop the Look
 *
 * Dependency-free custom element that connects the hotspots (drawn over the
 * image) to the product carousel (rk-carousel-element).
 *
 * Each product card is a carousel slide carrying `data-x` / `data-y` (the
 * hotspot coordinates, resolved in the block). On connect we build one hotspot
 * dot per slide and wire it to the carousel:
 *   - click a hotspot  -> carousel.slideTo(index)
 *   - swipe the carousel -> the matching hotspot becomes active
 *
 * The Swiper instance arrives asynchronously (the carousel lazy-loads on
 * visibility), so we listen for the bubbling `rk:carousel:init` event.
 */
class RkShopTheLook extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector('rk-carousel-element');
    this.hotspotsContainer = this.querySelector('[data-stl-hotspots]');
    this.slides = Array.from(this.querySelectorAll('[data-stl-card]'));

    this.buildHotspots();

    // Sync the active hotspot when the carousel changes slide.
    this.onCarouselInit = this.onCarouselInit.bind(this);
    this.addEventListener('rk:carousel:init', this.onCarouselInit);
  }

  disconnectedCallback() {
    this.removeEventListener('rk:carousel:init', this.onCarouselInit);
    this.swiper?.off?.('slideChange', this.onSlideChange);
  }

  buildHotspots() {
    if (!this.hotspotsContainer) return;
    this.hotspotsContainer.textContent = '';
    this.hotspots = this.slides.map((slide, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rk-stl-hotspot';
      button.dataset.index = String(index);
      button.style.setProperty('--x', `${slide.dataset.x || 50}%`);
      button.style.setProperty('--y', `${slide.dataset.y || 50}%`);
      button.setAttribute('aria-label', `Ver produto ${index + 1}`);
      button.addEventListener('click', () => this.goTo(index));
      this.hotspotsContainer.appendChild(button);
      return button;
    });
    this.setActive(0);
  }

  /** @param {CustomEvent} event */
  onCarouselInit(event) {
    this.swiper = event.detail?.swiper;
    if (!this.swiper) return;
    this.onSlideChange = () => this.setActive(this.swiper.realIndex ?? this.swiper.activeIndex ?? 0);
    this.swiper.on('slideChange', this.onSlideChange);
    this.setActive(this.swiper.realIndex ?? 0);
  }

  /** @param {number} index */
  goTo(index) {
    if (this.carousel && typeof this.carousel.slideTo === 'function') {
      this.carousel.slideTo(index);
    }
    this.setActive(index);
  }

  /** @param {number} index */
  setActive(index) {
    if (!this.hotspots) return;
    for (const hotspot of this.hotspots) {
      hotspot.classList.toggle('is-active', Number(hotspot.dataset.index) === index);
    }
  }
}

if (!customElements.get('rk-shop-the-look')) {
  customElements.define('rk-shop-the-look', RkShopTheLook);
}
